/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode, useEffect } from 'react';

import { MDXProvider } from '@mdx-js/react';

import { useHeadings } from '@af/design-system-docs-ui/heading-context';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image/image';
import { token } from '@atlaskit/tokens';

import { experiences, interactionModes } from './_content/_experiences';

const styles = cssMap({
	body: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.500'),
		maxWidth: '756px',
		'@media (min-width: 90rem)': { minWidth: '756px' },
	},
	section: { display: 'flex', flexDirection: 'column', gap: token('space.200') },
	sectionLarge: { gap: token('space.200') },
	chapter: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		marginBlockStart: token('space.400'),
	},
	sectionNarrow: { maxWidth: '709px' },
	textGroup: { display: 'flex', flexDirection: 'column', gap: token('space.100') },
	paragraph: { marginBlock: token('space.0'), font: token('font.body') },
	list: { marginBlock: token('space.0'), paddingInlineStart: token('space.300') },
	introList: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
		marginBlock: token('space.0'),
		paddingInlineStart: token('space.300'),
	},
	listItem: { marginBlock: token('space.0'), font: token('font.body') },
	heading: {
		scrollMarginTop: token('space.1000'),
	},
	image: { display: 'block', width: '100%', height: 'auto', minWidth: 0 },
	grid: {
		display: 'grid',
		columnGap: token('space.300'),
		rowGap: token('space.150'),
		gridTemplateColumns: 'minmax(0, 1fr)',
		'@media (min-width: 30rem)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
		'@media (min-width: 64rem)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
	},
	modesGrid: {
		display: 'grid',
		columnGap: token('space.300'),
		rowGap: token('space.150'),
		gridTemplateColumns: 'minmax(0, 1fr)',
		'@media (min-width: 30rem)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
	},
	card: { display: 'flex', flexDirection: 'column', gap: token('space.150') },
	cardText: { display: 'flex', flexDirection: 'column', gap: token('space.050') },
	cardHeading: {
		marginBlock: token('space.0'),
		font: token('font.body'),
		fontWeight: token('font.weight.bold'),
	},
});

/**
 * A section owns its internal spacing.
 */
export const Section = ({
	children,
	large = false,
	narrow = false,
}: {
	children: ReactNode;
	large?: boolean;
	narrow?: boolean;
}): React.JSX.Element => (
	<section css={[styles.section, large && styles.sectionLarge, narrow && styles.sectionNarrow]}>
		{children}
	</section>
);

/**
 * Closely related paragraphs and introductory illustrations use an 8px gap.
 */
export const TextGroup = ({ children }: { children: ReactNode }): React.JSX.Element => (
	<div css={styles.textGroup}>{children}</div>
);

const contentsLabels: Record<string, string> = {
	'a-new-standard-for-human-agent-interaction': 'About',
	'our-beliefs-about-agentic-experiences': 'Beliefs',
	'the-modes-of-human-agent-interaction': 'Interaction modes',
	'identity-shared-presence': 'Identity & presence',
	'real-time-async-collaboration': 'Collaboration',
	'context-governance': 'Governance',
	closing: 'Closing',
	related: 'Related',
};

const SectionHeading = ({
	id,
	children,
	depth,
}: {
	id?: string;
	children: ReactNode;
	depth: 2 | 3;
}): React.JSX.Element => {
	const { addHeading, removeHeading } = useHeadings();
	const headingId = (
		id ||
		String(children)
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
	)
		.replace(/-+/g, '-')
		.replace(/^-|-$/g, '');
	useEffect(() => {
		const label = contentsLabels[headingId];
		if (!label) {
			return;
		}
		const heading = {
			id: headingId,
			value: label,
			depth: 2,
		};
		addHeading(heading);
		return () => removeHeading(heading);
	}, [headingId, addHeading, removeHeading]);
	return (
		<div id={headingId} css={styles.heading}>
			<Heading
				as={depth === 2 ? 'h2' : 'h3'}
				size={depth === 2 && headingId !== 'related' ? 'xlarge' : 'large'}
			>
				{children}
			</Heading>
		</div>
	);
};

const components = {
	h2: (props: { id: string; children: ReactNode }) => <SectionHeading {...props} depth={2} />,
	h3: (props: { id: string; children: ReactNode }) => <SectionHeading {...props} depth={3} />,
	p: ({ children }: { children: ReactNode }) => <p css={styles.paragraph}>{children}</p>,
	ol: ({ children }: { children: ReactNode }) => <ol css={styles.list}>{children}</ol>,
	ul: ({ children }: { children: ReactNode }) => <ul css={styles.introList}>{children}</ul>,
	li: ({ children }: { children: ReactNode }) => <li css={styles.listItem}>{children}</li>,
	img: ({ src, srcDark, alt }: { src: string; srcDark?: string; alt: string }) => (
		<Image src={src} srcDark={srcDark} alt={alt} css={styles.image} />
	),
};

/**
 * AMP-specific spacing and typography, without changing other documentation pages.
 */
export const ContentLayout = ({ children }: { children: ReactNode }): React.JSX.Element => (
	<MDXProvider components={components}>
		<div css={styles.body}>{children}</div>
	</MDXProvider>
);

export const Experiences = ({ modes = false }: { modes?: boolean }): React.JSX.Element => (
	<div css={[!modes && styles.grid, modes && styles.modesGrid]}>
		{(modes ? interactionModes : experiences).map(({ title, description, image }) => (
			<div key={title} css={styles.card}>
				{image && (
					<Image src={image.src} srcDark={image.srcDark} alt={image.alt} css={styles.image} />
				)}
				<div css={styles.cardText}>
					<h5 css={styles.cardHeading}>{title}</h5>
					<p css={styles.paragraph}>{description}</p>
				</div>
			</div>
		))}
	</div>
);

export const Chapter = ({ children }: { children: ReactNode }): React.JSX.Element => (
	<div css={styles.chapter}>{children}</div>
);
